import { preloadAllAssetsAsync, preloadDeferredAssetsAsync, preloadPngUpgrades } from './assets';

const MANIFEST_URL = '/asset-manifest.json';
const WARM_TIMEOUT_MS = 2 * 60 * 1000;

type ProgressHandler = (loaded: number, total: number) => void;
type WarmScope = 'critical' | 'deferred';

function canUseServiceWorker() {
  return typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    window.isSecureContext;
}

function canWarmThroughServiceWorker() {
  return canUseServiceWorker() && !!navigator.serviceWorker.controller;
}

function waitForActiveWorker(registration: ServiceWorkerRegistration): Promise<ServiceWorker> {
  const current = registration.active ?? registration.waiting ?? registration.installing;
  if (current && current.state === 'activated') {
    return Promise.resolve(current);
  }

  return new Promise((resolve, reject) => {
    const worker = current;
    if (!worker) {
      reject(new Error('No service worker instance available.'));
      return;
    }

    const handleStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', handleStateChange);
        resolve(worker);
      }
    };

    worker.addEventListener('statechange', handleStateChange);
  });
}

async function warmThroughServiceWorker(scope: WarmScope, onProgress?: ProgressHandler): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const worker = await waitForActiveWorker(registration);

  return new Promise((resolve, reject) => {
    let settled = false;
    let firstMessageReceived = false;
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timed out while warming asset cache.'));
    }, WARM_TIMEOUT_MS);

    const connectionTimer = window.setTimeout(() => {
      if (!firstMessageReceived) {
        cleanup();
        reject(new Error('Service worker did not respond within 3 seconds.'));
      }
    }, 3000);

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.clearTimeout(connectionTimer);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'ASSET_CACHE_PROGRESS' || data.scope !== scope) {
        return;
      }

      firstMessageReceived = true;

      if (typeof data.loaded === 'number' && typeof data.total === 'number') {
        if (data.total === 0) {
           onProgress?.(1, 1); // 100% if no assets
        } else {
           onProgress?.(data.loaded, data.total);
        }
      }

      if (data.status === 'done') {
        cleanup();
        resolve();
      }

      if (data.status === 'error') {
        const errorMessage = typeof data.error === 'string' ? data.error : 'Unknown asset cache error.';
        cleanup();
        reject(new Error(errorMessage));
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    worker.postMessage({ type: 'WARM_CACHE', manifestUrl: MANIFEST_URL, scope });
  });
}

export async function warmAllGameAssets(onProgress?: ProgressHandler): Promise<void> {
  if (!canWarmThroughServiceWorker()) {
    await preloadAllAssetsAsync(onProgress);
    warmDeferredGameAssets().catch(err =>
      console.warn('[assetCache] Background deferred warmup failed.', err)
    );
    preloadPngUpgrades(2).catch(err =>
      console.warn('[assetCache] Background PNG upgrade failed.', err)
    );
    return;
  }

  try {
    await warmThroughServiceWorker('critical', onProgress);
    warmDeferredGameAssets().catch(err =>
      console.warn('[assetCache] Background deferred warmup failed.', err)
    );
    preloadPngUpgrades(2).catch(err =>
      console.warn('[assetCache] Background PNG upgrade failed.', err)
    );
  } catch (error) {
    console.warn('[assetCache] Falling back to in-memory preload.', error);
    await preloadAllAssetsAsync(onProgress);
    warmDeferredGameAssets().catch(err =>
      console.warn('[assetCache] Background deferred warmup failed.', err)
    );
    preloadPngUpgrades(2).catch(err =>
      console.warn('[assetCache] Background PNG upgrade failed.', err)
    );
  }
}

export async function warmDeferredGameAssets(): Promise<void> {
  if (!canWarmThroughServiceWorker()) {
    await preloadDeferredAssetsAsync({ concurrency: 4 });
    return;
  }

  try {
    await warmThroughServiceWorker('deferred');
  } catch (error) {
    console.warn('[assetCache] Deferred warmup fell back to in-memory preload.', error);
    await preloadDeferredAssetsAsync({ concurrency: 4 });
  }
}